import React from 'react';
import { useParams } from 'react-router-dom';
import { InnerLayout } from '../components/layout/InnerLayout';
import { HedgehogSpinner } from '../components/brand/HedgehogSpinner';
import { Button } from '../components/ui/Button';
import { ArrowLeft } from 'lucide-react';
import { useTenantDetail } from './tenant/hooks/useTenantDetail';
import { TenantProfileCard } from './tenant/components/TenantProfileCard';
import { TenantOrganizationsCard } from './tenant/components/TenantOrganizationsCard';
import { TenantSecurityCard } from './tenant/components/TenantSecurityCard';
import { TenantDangerZone } from './tenant/components/TenantDangerZone';

export const TenantDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const state = useTenantDetail(id);

  if (state.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <HedgehogSpinner size="md" label="Loading tenant profile..." />
      </div>
    );
  }

  if (!state.tenant) {
    return null;
  }

  return (
    <InnerLayout
      title={state.tenant.full_name || state.tenant.email}
      description={`Manage tenant account, roles, organization memberships, and quotas.`}
      actions={[
        {
          label: 'Back to Tenants',
          icon: ArrowLeft,
          variant: 'secondary',
          to: '/admin/tenants',
        },
      ]}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-6">
          <TenantProfileCard
            tenant={state.tenant}
            fullName={state.fullName}
            setFullName={state.setFullName}
            email={state.email}
            setEmail={state.setEmail}
            isActive={state.isActive}
            setIsActive={state.setIsActive}
            isSuperadmin={state.isSuperadmin}
            setIsSuperadmin={state.setIsSuperadmin}
            isSaving={state.isSaving}
            isSelf={state.isSelf}
            onSave={state.handleSaveProfile}
            onOpenResetModal={() => state.setResetModalOpen(true)}
          />

          <TenantSecurityCard
            resetModalOpen={state.resetModalOpen}
            onCloseResetModal={() => {
              state.setResetModalOpen(false);
              state.setGeneratedResetUrl(null);
            }}
            newPassword={state.newPassword}
            setNewPassword={state.setNewPassword}
            isResettingPassword={state.isResettingPassword}
            generatedResetUrl={state.generatedResetUrl}
            onResetPassword={state.handleResetPassword}
            onOpenResetModal={() => state.setResetModalOpen(true)}
          />

          <TenantDangerZone
            tenant={state.tenant}
            isSelf={state.isSelf}
            isDeletingUser={state.isDeletingUser}
            onDeleteTenant={state.handleDeleteTenant}
          />
        </div>

        <div className="lg:col-span-7 space-y-6">
          <TenantOrganizationsCard
            organizations={state.organizations}
            plans={state.plans}
            onOpenAddModal={() => state.setAddOrgModalOpen(true)}
            onEditOrg={(org) => {
              state.setEditingOrg(org);
              state.setEditTier(org.plan_tier || 'free');
              state.setEditMaxSubmissions(org.max_monthly_submissions);
              state.setEditMaxTables(org.max_tables);
              state.setEditMaxSeats(org.max_team_seats);
            }}
            onRemoveOrg={state.handleRemoveFromOrg}
            editingOrg={state.editingOrg}
            onCloseEditModal={() => state.setEditingOrg(null)}
            editTier={state.editTier}
            setEditTier={state.setEditTier}
            editMaxSubmissions={state.editMaxSubmissions}
            setEditMaxSubmissions={state.setEditMaxSubmissions}
            editMaxTables={state.editMaxTables}
            setEditMaxTables={state.setEditMaxTables}
            editMaxSeats={state.editMaxSeats}
            setEditMaxSeats={state.setEditMaxSeats}
            isUpdatingOrg={state.isUpdatingOrg}
            onSaveOrgQuotas={state.handleUpdateOrgQuotas}
            addOrgModalOpen={state.addOrgModalOpen}
            onCloseAddModal={() => {
              state.setAddOrgModalOpen(false);
              state.setSelectedOrg(null);
              state.setOrgSearchQuery('');
            }}
            orgSearchQuery={state.orgSearchQuery}
            onSearchOrgs={state.handleSearchOrgs}
            orgSearchResults={state.orgSearchResults}
            selectedOrg={state.selectedOrg}
            onSelectOrg={state.setSelectedOrg}
            selectedRole={state.selectedRole}
            setSelectedRole={state.setSelectedRole}
            isSearchingOrgs={state.isSearchingOrgs}
            isAddingOrg={state.isAddingOrg}
            onAssignToOrg={state.handleAssignToOrg}
          />
        </div>
      </div>
    </InnerLayout>
  );
};
